<template>
  <div>
    <div class="mb-4">
      <template v-if="!orderFound && !submitted">
        <p>Enter your order ID, email address and zip / postcode.</p>
      </template>
      <p v-else-if="!orderFound && submitted">
        <template v-if="project() === 'kitlocker-main-site-364199'">
          Sorry, we were unable to find an order matching that ID. Try
          <a href="https://myorders.kitlocker.shop/login/"
            >https://myorders.kitlocker.shop/login/</a
          >.
        </template>
      </p>
    </div>
    <tml-form ref="findOrderForm" form-name="find-order-form">
      <div v-if="!submitted">
        <tml-input
          v-model="orderId"
          placeholder="Order ID (e.g. 123123)"
          type="text"
          field-name="orderId"
          :validation-rules="{required: true}"
          @submit="onSubmitTracking"
        />
        <tml-input
          v-model="email"
          :validation-rules="{required: true, email: true}"
          placeholder="Email address"
          field-name="emailAddress"
          type="email"
          @submit="onSubmitTracking"
        />
        <tml-input
          v-model="zip"
          :validation-rules="{required: false}"
          placeholder="Postcode / zip"
          @submit="onSubmitTracking"
        />
        <tml-re-captcha
          ref="captcha"
          :class="{'mb-4': recaptchaMargin}"
          action="subdomains_track_order"
          @rendered="addMargin"
          @submit="onSubmitTracking"
        />
        <tml-button
          fill
          :loading="loading"
          text="Submit"
          @click="onSubmitTracking"
        />
      </div>
    </tml-form>
    <div v-if="orderFound">
      <div v-if="order.status === 'new'">
        <p style="font-weight: bold">Order {{ order.id }} - Not Paid</p>
        <p>The payment for this order has not yet been taken.</p>
      </div>
      <div v-else-if="order.status === 'paid'">
        <p style="font-weight: bold">Order {{ order.id }} - Paid</p>
        <p>
          We've received payment for your order and it will be dispatched soon.
        </p>
        <p>
          We estimate that it will be with you between the
          <b>{{ order.eta.earliest }}</b> and the <b>{{ order.eta.latest }}</b>
        </p>
      </div>
      <div
        v-else-if="
          order.status === 'processing' ||
          order.status === 'consolidated' ||
          order.status === 'printed' ||
          order.status === 'label_printed' ||
          order.status === 'packing' ||
          order.status === 'issue'
        "
      >
        <p style="font-weight: bold">Order {{ order.id }} - Processing</p>
        <p>
          Your order is currently being processed and will be shipped shortly.
        </p>
        <p>
          We estimate that it will be with you between the
          <b>{{ order.eta.earliest }}</b> and the <b>{{ order.eta.latest }}</b>
        </p>
      </div>
      <div
        v-else-if="
          order.status === 'complete' ||
          order.status === 'exchanged' ||
          order.status === 'bespoke_shipped'
        "
      >
        <div v-if="order.shippingMethod.code === 'store_collect'">
          <p style="font-weight: bold">
            Order {{ order.id }} - Ready For Collection
          </p>
        </div>
        <div v-else>
          <p style="font-weight: bold">Order {{ order.id }} - Dispatched</p>
          <!-- <div v-if="order.isLate"> -->
          <div v-if="order.maxResendDaysDifference > 0">
            <p>
              Our records show that your order has been dispatched and is on its
              way to you.
            </p>
            <p v-if="multiFulfillments">
              Please note that your order has been split into more than one
              parcel, so your items may arrive separately. A tracking link has
              been provided below for each parcel so you can follow the progress
              for each delivery.
            </p>
            <p>
              We expect
              {{ multiFulfillments ? 'all parcels' : 'your order' }}
              to arrive by <b>{{ order.maxExpectedDeliveryDate }}</b> at the
              latest.
            </p>
            <p v-if="order.trackingUrl">
              You can check the latest delivery
              {{ multiFulfillments ? 'updates' : 'update' }} at any time by
              clicking the
              {{
                multiFulfillments
                  ? '"Track Parcel" buttons'
                  : '"Track My Order" button'
              }}
              below.
            </p>
            <p>
              If {{ multiFulfillments ? 'any part of ' : '' }}your order has not
              arrived by <b>{{ order.maxExpectedDeliveryDatePlusOne }}</b
              >, please return to this form for the next steps. We’ll guide you
              through how to contact our Customer Service Team, who will be
              happy to investigate further.
            </p>
            <p>
              Our Customer Service Team is available Mon to Fri, 9am to 5pm
              (excluding weekends and Bank Holidays).
            </p>
          </div>
          <div v-else-if="order.maxResendDaysDifference <= 0">
            <p>
              Our records show that your order was dispatched on
              {{ order.dispatchDate }} and should have been delivered by now.
            </p>
            <p v-if="multiFulfillments">
              Please note that your order has been split into more than one
              parcel, so your items may arrive separately. A tracking link has
              been provided below for each parcel so you can follow the progress
              for each delivery.
            </p>
            <p v-if="!multiFulfillments && order.trackingUrl">
              You can track your order and view the latest delivery update by
              clicking
              <tml-anchor
                class="font-bold"
                text="here"
                :href="order.trackingUrl"
              />.
            </p>
            <p>
              If your order still hasn’t arrived, please click the “Still not
              arrived?” button below and complete the contact form so we can
              investigate this for you.
            </p>
            <p>
              To help us look into this as quickly as possible, please confirm
              the delivery address used for your order. This will help us rule
              out any address issues that may have affected delivery.
            </p>
            <p>
              Your enquiry will be sent directly to our Customer Service Team,
              who will investigate and do everything they can to resolve this
              for you.
            </p>
            <p>
              Our Customer Service Team are available Mon-Fri, 9am-5pm
              (excluding weekends and Bank Holidays) and will be in touch as
              soon as possible.
            </p>
          </div>
          <div>
            <tml-button
              v-if="order.trackingUrls.length === 1"
              primary
              text="Track my order"
              target="_blank"
              :href="order.trackingUrls[0]"
            />
            <template v-else-if="multiFulfillments">
              <div class="flex gap-2">
                <tml-button
                  v-for="(trackingUrl, index) in order.trackingUrls"
                  :key="index"
                  primary
                  :text="`Track parcel  #${index + 1}`"
                  target="_blank"
                  :href="trackingUrl"
                />
              </div>
            </template>
          </div>

          <tml-button
            v-scroll-to="{
              el: '#contact-form',
              offset: -50,
            }"
            class="mt-4"
            text="Still not arrived?"
            @click="emitNotArrived"
          />
        </div>
      </div>
      <div v-else-if="order.status === 'refunded'">
        <p style="font-weight: bold">Order {{ order.id }} - Refunded</p>
      </div>
    </div>
  </div>
</template>

<script>
import {formatUrl} from '@teemill/common/helpers';

export default {
  name: 'HelpTrackOrder',

  data() {
    return {
      order: null,
      email: null,
      zip: null,
      orderFound: false,
      loading: false,
      submitted: false,
      recaptchaMargin: false,
    };
  },

  computed: {
    multiFulfillments() {
      return this.order?.trackingUrls.length > 1;
    },
  },

  methods: {
    onSubmitTracking() {
      if (this.loading) {
        return;
      }

      this.$refs.findOrderForm.validateAll().then(success => {
        if (!success) {
          return;
        }

        this.loading = true;

        this.$refs.captcha.recaptcha
          .ifHuman(recaptcha => {
            this.axios
              .post(formatUrl('/omnis/v3/contact/track-order/'), {
                order: this.orderId,
                email: this.email,
                zip: this.zip,
                v3captchaToken: recaptcha.v3Token,
                v2captchaToken: recaptcha.v2Token,
              })
              .success(data => {
                if (data) {
                  if ('message' in data) {
                    snackbar.error(data.message);
                  } else {
                    this.order = data;
                    this.orderFound = true;
                  }
                }
              })
              .validation()
              .oops()
              .finally(() => {
                this.loading = false;
                this.submitted = true;
              });
          })
          .ifBot(() => {
            this.loading = false;
          });
      });
    },

    emitNotArrived() {
      this.$emit('not-arrived', 1);
    },

    project() {
      return (
        this.$project?.active?.id || this.$store.state.subdomain?.project?.id
      );
    },

    addMargin() {
      this.recaptchaMargin = true;
    },
  },
};
</script>

<style lang="scss" scoped>
p {
  margin-top: 1em;
  margin-bottom: 1em;
}

h5 {
  margin-top: 1em;
  margin-bottom: 1em;
}
</style>
